import type { Task } from "@app/core";
import { parseRRule, recomputeShapedRrule, resolveEffectiveRrule } from "@app/core";
import { useRecurringConfirmStore, useTasksStore } from "../../stores/index.js";

/**
 * The single place the "this / this-and-following / all" recurring-edit dispatch
 * lives. Every drag-move, resize, and delete surface (week/day grid, month grid,
 * all-day row, context menu) funnels through these so the confirm prompt and the
 * load-bearing "all = shift the base by the drag delta" math exist exactly once.
 *
 * Callers resolve `source` (via `resolveSource`) and the occurrence key (via
 * `occurrenceIsoOf` / `splitVirtualId`) before calling — those are one-liners and
 * differ per surface; the branching that used to be copy-pasted is here.
 */
export function useRecurringTaskActions() {
  const reschedule = useTasksStore((s) => s.reschedule);
  const rescheduleSeries = useTasksStore((s) => s.rescheduleSeries);
  const rescheduleOccurrence = useTasksStore((s) => s.rescheduleOccurrence);
  const resizeOccurrence = useTasksStore((s) => s.resizeOccurrence);
  const resizeSeries = useTasksStore((s) => s.resizeSeries);
  const resizeSeriesFromTop = useTasksStore((s) => s.resizeSeriesFromTop);
  const detachWithPatch = useTasksStore((s) => s.detachWithPatch);
  const removeSeriesOccurrence = useTasksStore((s) => s.removeSeriesOccurrence);
  const editSeriesFromOccurrence = useTasksStore((s) => s.editSeriesFromOccurrence);
  const edit = useTasksStore((s) => s.edit);
  const ask = useRecurringConfirmStore((s) => s.ask);
  // Read rules LIVE at call time (not a captured slice): these handlers are held
  // by memoized grid columns that can skip re-rendering across an edit, so a
  // closed-over `rules` could be stale. Store action refs are stable, so they're
  // safe to close over — only the state slices must be read fresh.
  const liveRules = () => useTasksStore.getState().rules;

  /** "all" semantics: translate the base task's dtstart by the same delta the
   *  user dragged this occurrence, preserving the rrule shape. */
  function shiftedBaseDueAt(source: Task, oldOcc: string, newIso: string): string | null {
    if (!source.due_at) return null;
    const delta = new Date(newIso).getTime() - new Date(oldOcc).getTime();
    return new Date(new Date(source.due_at).getTime() + delta).toISOString();
  }

  function rruleForMovedTail(source: Task, oldOcc: string, newIso: string): string | null {
    if (!source.recurrence_id) return null;
    const rule = liveRules()[source.recurrence_id];
    if (!rule) return null;
    const effective = resolveEffectiveRrule(rule, oldOcc, useTasksStore.getState().overrides[rule.id] ?? []);
    return shapeChanged(effective, new Date(oldOcc), new Date(newIso))
      ? recomputeShapedRrule(effective, new Date(newIso))
      : effective;
  }

  async function rescheduleTask(opts: {
    source: Task;
    oldOcc: string | null;
    newIso: string;
    isVirtual: boolean;
  }): Promise<void> {
    const { source, oldOcc, newIso, isVirtual } = opts;
    if (!source.recurrence_id) { await reschedule(source.id, newIso); return; }
    if (!oldOcc) return;
    const choice = await ask({ kind: "reschedule", task: source, sourceId: source.id, oldOccurrence: oldOcc, newDueAt: newIso, isVirtual });
    if (choice === "cancel") return;
    if (choice === "all") {
      const rule = liveRules()[source.recurrence_id];
      const base = shiftedBaseDueAt(source, oldOcc, newIso);
      if (!rule || !base) return;
      await edit(source.id, { due_at: base }, parseRRule(rule.rrule));
      return;
    }
    if (choice === "series") { await rescheduleSeries(source.id, new Date(oldOcc), new Date(newIso)); return; }
    await rescheduleOccurrence(source.id, oldOcc, newIso);
  }

  /** Bottom-edge resize: duration only. `occKey` is the occurrence's effective
   *  due (`t.due_at`), matching the historical single/series resize key. */
  async function resizeTask(opts: {
    source: Task;
    occKey: string | null;
    newMinutes: number;
    isVirtual: boolean;
  }): Promise<void> {
    const { source, occKey, newMinutes, isVirtual } = opts;
    if (!source.recurrence_id) { await edit(source.id, { duration_minutes: newMinutes }); return; }
    const choice = await ask({ kind: "resize", task: source, sourceId: source.id, newDurationMinutes: newMinutes, isVirtual });
    if (choice === "cancel") return;
    if (choice === "single" && occKey) { await resizeOccurrence(source.id, occKey, newMinutes); return; }
    if (choice === "series" && occKey) { await resizeSeries(source.id, occKey, newMinutes); return; }
    if (choice === "all") { await edit(source.id, { duration_minutes: newMinutes }); return; }
  }

  /** Top-edge resize: moves start AND duration so the bottom edge stays put.
   *  `occKey` is the RECURRENCE-ID (from the virtual id), not the effective due. */
  async function resizeTaskFromTop(opts: {
    source: Task;
    occKey: string;
    newStartIso: string;
    newMinutes: number;
    isVirtual: boolean;
  }): Promise<void> {
    const { source, occKey, newStartIso, newMinutes, isVirtual } = opts;
    if (!source.recurrence_id) { await edit(source.id, { due_at: newStartIso, duration_minutes: newMinutes }); return; }
    const choice = await ask({ kind: "resize", task: source, sourceId: source.id, newDurationMinutes: newMinutes, isVirtual });
    if (choice === "cancel") return;
    if (choice === "single") { await detachWithPatch(source.id, occKey, { due_at: newStartIso, duration_minutes: newMinutes }); return; }
    if (choice === "series") { await resizeSeriesFromTop(source.id, occKey, newStartIso, newMinutes); return; }
    if (choice === "all") {
      const rule = liveRules()[source.recurrence_id];
      const base = shiftedBaseDueAt(source, occKey, newStartIso);
      if (!rule || !base) return;
      await edit(source.id, { due_at: base, duration_minutes: newMinutes }, parseRRule(rule.rrule));
      return;
    }
  }

  async function convertTaskToAllDay(opts: {
    source: Task;
    oldOcc: string | null;
    newIso: string;
    durationMinutes: number;
    isVirtual: boolean;
  }): Promise<void> {
    const { source, oldOcc, newIso, durationMinutes, isVirtual } = opts;
    const patch = { due_at: newIso, all_day: true, duration_minutes: durationMinutes };
    if (!source.recurrence_id) { await edit(source.id, patch); return; }
    if (!oldOcc) return;

    const choice = await ask({
      kind: "convert-all-day",
      task: source,
      sourceId: source.id,
      oldOccurrence: oldOcc,
      newDueAt: newIso,
      newDurationMinutes: durationMinutes,
      isVirtual,
    });
    if (choice === "cancel") return;
    if (choice === "single") { await detachWithPatch(source.id, oldOcc, patch); return; }
    if (choice === "series") {
      const newRrule = rruleForMovedTail(source, oldOcc, newIso);
      if (!newRrule) return;
      await editSeriesFromOccurrence(source.id, oldOcc, {
        newDueAt: newIso,
        all_day: true,
        duration_minutes: durationMinutes,
        newRrule,
      });
      return;
    }
    if (choice === "all") {
      const rule = liveRules()[source.recurrence_id];
      const base = shiftedBaseDueAt(source, oldOcc, newIso);
      if (!rule || !base) return;
      await edit(source.id, { ...patch, due_at: base }, parseRRule(rule.rrule));
    }
  }

  async function convertTaskFromAllDay(opts: {
    source: Task;
    oldOcc: string | null;
    newIso: string;
    durationMinutes: number;
    isVirtual: boolean;
  }): Promise<void> {
    const { source, oldOcc, newIso, durationMinutes, isVirtual } = opts;
    const patch = { due_at: newIso, all_day: false, duration_minutes: durationMinutes };
    if (!source.recurrence_id) { await edit(source.id, patch); return; }
    if (!oldOcc) return;
    const choice = await ask({
      kind: "reschedule",
      task: source,
      sourceId: source.id,
      oldOccurrence: oldOcc,
      newDueAt: newIso,
      isVirtual,
    });
    if (choice === "cancel") return;
    if (choice === "single") { await detachWithPatch(source.id, oldOcc, patch); return; }
    if (choice === "series") {
      const newRrule = rruleForMovedTail(source, oldOcc, newIso);
      if (!newRrule) return;
      await editSeriesFromOccurrence(source.id, oldOcc, {
        newDueAt: newIso,
        all_day: false,
        duration_minutes: durationMinutes,
        newRrule,
      });
      return;
    }
    if (choice === "all") {
      const rule = liveRules()[source.recurrence_id];
      const base = shiftedBaseDueAt(source, oldOcc, newIso);
      if (!rule || !base) return;
      await edit(source.id, { ...patch, due_at: base }, parseRRule(rule.rrule));
    }
  }

  async function removeTask(opts: {
    task: Task;
    sourceId: string;
    occurrenceIso: string;
    isVirtual: boolean;
  }): Promise<void> {
    const { task, sourceId, occurrenceIso, isVirtual } = opts;
    const choice = await ask({ kind: "delete", task, sourceId, occurrenceIso, isVirtual });
    if (choice === "cancel") return;
    if (choice === "all") { await removeSeriesOccurrence(sourceId, occurrenceIso, "all"); return; }
    if (choice === "series") { await removeSeriesOccurrence(sourceId, occurrenceIso, "following"); return; }
    await removeSeriesOccurrence(sourceId, occurrenceIso, "single");
  }

  return { rescheduleTask, resizeTask, resizeTaskFromTop, convertTaskToAllDay, convertTaskFromAllDay, removeTask };
}

function shapeChanged(rrule: string, oldOcc: Date, newOcc: Date): boolean {
  const input = parseRRule(rrule);
  if (input.byDay && input.byDay.length > 0 && oldOcc.getDay() !== newOcc.getDay()) return true;
  if (input.byMonthDay && oldOcc.getDate() !== newOcc.getDate()) return true;
  return false;
}
